import React, { useMemo, useRef } from 'react';

import { convertDobToTimestamp } from '@utils/date';
import PropTypes from 'prop-types';

import FormCheckbox from '@ui/form/FormCheckbox';
import FormNumberTextField from '@ui/form/FormNumberTextField';
import FormTextField from '@ui/form/FormTextField';
import Grid from '@ui/Grid';
import Button from '@ui/inputs/Button';

import Cms from '@components/Cms/Cms';

import { registerFormSchema } from './registerForm.schema';
import useCms from '../Cms/useCms';
import Form from '../Form/Form';

const RegisterForm = ({ onSubmit, loading, fullWidthButton, isDobRequired }) => {
    const inputMonth = useRef();
    const inputDay = useRef();
    const cms = useCms();
    const currentYear = new Date().getFullYear();
    const registerSchema = useMemo(() => registerFormSchema(cms, currentYear), [cms, currentYear]);

    const handleOnSubmit = form => {
        const { email, password, isSubscribed, isLegalAccepted, profile } = form;
        const result = {
            email,
            password,
            isSubscribed,
            isLegalAccepted,
            profile: {
                ...profile,
                dob: convertDobToTimestamp(profile?.dob),
            },
        };
        onSubmit(result);
    };

    const moveToMonthInput = () => {
        inputMonth.current.focus();
    };

    const moveToDayInput = () => {
        inputDay.current.focus();
    };

    const defaultValues = {
        profile: {
            dob: {
                isDobRequired,
            },
        },
    };

    return (
        <Form onSubmit={handleOnSubmit} schema={registerSchema} defaultValues={defaultValues}>
            <FormTextField disabled={loading} name="email" label={cms[25]} type="email" />
            <FormTextField disabled={loading} name="password" label={cms[26]} type="password" />
            <FormTextField disabled={loading} name="passwordConfirmation" label={cms[364]} type="password" />
            <FormTextField disabled={loading} name="profile.lastName" label={cms[300]} type="text" />
            <FormTextField disabled={loading} name="profile.firstName" label={cms[24]} type="text" />
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <FormNumberTextField
                        disabled={loading}
                        digit={4}
                        onIsValidListener={moveToMonthInput}
                        label={cms[339]}
                        name="profile.dob.year"
                        type="number"
                        InputProps={{ inputProps: { min: 1900, max: currentYear } }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormNumberTextField
                        ref={inputMonth}
                        onIsValidListener={moveToDayInput}
                        digit={2}
                        disabled={loading}
                        label={cms[341]}
                        name="profile.dob.month"
                        type="number"
                        InputProps={{ inputProps: { min: 1, max: 12 } }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormNumberTextField
                        ref={inputDay}
                        digit={2}
                        disabled={loading}
                        label={cms[343]}
                        name="profile.dob.day"
                        type="number"
                        InputProps={{ inputProps: { min: 1, max: 31 } }}
                    />
                </Grid>
            </Grid>
            <Grid sx={{ marginBottom: 2 }}>
                <FormCheckbox
                    disabled={loading}
                    name="isLegalAccepted"
                    label={<Cms cmsId={2007} html className="cms-link" />}
                />
            </Grid>
            <Grid sx={{ marginBottom: 2 }}>
                <FormCheckbox disabled={loading} name="isSubscribed" label={cms[492]} />
            </Grid>
            <Button fullWidth={fullWidthButton} loading={loading} disabled={loading} type="submit">
                {cms[93]}
            </Button>
        </Form>
    );
};

RegisterForm.propTypes = {
    onSubmit: PropTypes.func,
    loading: PropTypes.bool,
    fullWidthButton: PropTypes.bool,
    isDobRequired: PropTypes.bool,
};

export default RegisterForm;
